/**
 * Shared in-flight-promise cache for GET /api/ai/session/me, consumed by
 * useCompatMember and useCompatOrganization. Both hooks need data from the
 * same endpoint but mount independently at boot (often ~9 times in the same
 * render pass), so without dedup each mount fires its own duplicate
 * request. Mirrors the refreshPromiseRef idiom in
 * EmbedAuthContext.refreshTokens.
 */

import axios from "../../config/axios";

interface SessionMeMember {
    member_id: string;
    email_address: string;
    name: string;
    roles: Array<{ role_id: string }>;
}

interface SessionMeOrganization {
    organization_id: string;
    organization_slug: string;
    organization_name?: string;
    trusted_metadata?: Record<string, any>;
}

export interface SessionMeResponse {
    member: SessionMeMember;
    organization: SessionMeOrganization;
}

let inFlightPromise: Promise<SessionMeResponse> | null = null;
let resolvedResponse: SessionMeResponse | null = null;
let cacheGeneration = 0;

/**
 * Last resolved response, if any hook instance already fetched it this
 * session. Lets a hook instance that mounts after resolution (but before
 * its own localStorage-read effect has run) adopt the data synchronously,
 * e.g. via a useState lazy initializer.
 */
export function getCachedSessionMe(): SessionMeResponse | null {
    return resolvedResponse;
}

/** Fetches /api/ai/session/me, deduping concurrent callers onto one in-flight request. */
export function fetchSessionMe(): Promise<SessionMeResponse> {
    if (inFlightPromise) {
        return inFlightPromise;
    }

    // The one truthful place to log this fetch: hook instances log nothing
    // themselves (a dozen mounts coalescing here used to print a dozen
    // "Fetching…" lines for one actual request).
    console.log("[sessionMeCache] Fetching /api/ai/session/me");
    const requestGeneration = cacheGeneration;
    const requestPromise = axios
        .get<SessionMeResponse>("/api/ai/session/me")
        .then((response) => {
            if (requestGeneration !== cacheGeneration) {
                throw new Error("Session lookup was invalidated");
            }
            resolvedResponse = response.data;
            return response.data;
        })
        .finally(() => {
            if (inFlightPromise === requestPromise) {
                inFlightPromise = null;
            }
        });

    inFlightPromise = requestPromise;
    return inFlightPromise;
}

/** Clears the cached/in-flight response, e.g. on logout, so the next fetch hits the network again. */
export function clearSessionMeCache(): void {
    cacheGeneration += 1;
    resolvedResponse = null;
    inFlightPromise = null;
}
